import { usePlateEditorRef, usePlateId, PortalBody, createPlateUI, ELEMENT_MENTION as ELEMENT_MENTION$1, withProps, MentionElement, createParagraphPlugin, createBlockquotePlugin, createCodeBlockPlugin, createHeadingPlugin, createBoldPlugin, createItalicPlugin, createUnderlinePlugin, createStrikethroughPlugin, createCodePlugin, createComboboxPlugin, createMentionPlugin, createPlugins, Plate, MentionCombobox, getEditorString, escapeRegExp, insertNodes, isCollapsed, isWordAfterTrigger, isPointAtWordEnd, createPluginFactory } from '@udecode/plate';
import * as React from 'react';
import { useState as useState$1, useMemo as useMemo$1, useCallback as useCallback$1 } from 'react';
import { usePopper } from 'react-popper';
import { ReactEditor } from 'slate-react';
import { Avatar } from '@sketchnote/shared-components';
import { v4 } from 'uuid';
import { Editor, Transforms, Range } from 'slate';
const {
  useCallback,
  useRef,
  useEffect,
  useState
} = React;
const virtualReference = {
  getBoundingClientRect() {
    return {
      top: 10,
      left: 10,
      bottom: 20,
      right: 100,
      width: 90,
      height: 10
    };
  }

};

const MentionSelect = ({
  at,
  options,
  valueIndex,
  onClickMention,
  renderLabel = mentionable => mentionable.value,
  containerElement,
  id
}) => {
  const ref = useRef(null);
  const [, setPopUpdate] = useState(0);
  const editor = usePlateEditorRef(id || usePlateId() || undefined);
  const {
    styles,
    attributes,
    forceUpdate
  } = usePopper(virtualReference, ref.current, {
    placement: 'bottom-start',
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, 10]
      }
    }, {
      name: 'eventListeners',
      options: {
        scroll: !!at
      }
    }]
  });
  const setPosition = useCallback(() => {
    if (at && editor) {
      const domRange = ReactEditor.toDOMRange(editor, at);
      const rect = domRange.getBoundingClientRect();

      virtualReference.getBoundingClientRect = () => rect;

      forceUpdate === null || forceUpdate === void 0 ? void 0 : forceUpdate();
      setPopUpdate(s => s + 1);
    }
  }, [virtualReference, forceUpdate, at, editor]);
  useEffect(() => {
    ref && at && setPosition();
  }, [at, editor, setPosition]);
  useEffect(() => {
    containerElement === null || containerElement === void 0 ? void 0 : containerElement.addEventListener('scroll', setPosition);
    return () => containerElement === null || containerElement === void 0 ? void 0 : containerElement.removeEventListener('scroll', setPosition);
  }, [setPosition]);

  const handleOnMouseDown = (option, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onClickMention) onClickMention(editor, option);
  };

  const handleOnMouseDownWrapper = useCallback(e => {
    e.stopPropagation();
  }, []);
  const handleKeyDownWrapper = useCallback(e => {
    e.stopPropagation();
  }, []);

  if (!at || !options.length) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }

  return /*#__PURE__*/React.createElement(PortalBody, null, /*#__PURE__*/React.createElement("div", Object.assign({
    ref: ref,
    className: `max-h-304 absolute overflow-auto bg-white rounded shadow-md z-2000`,
    style: Object.assign({}, styles.popper),
    role: "button",
    tabIndex: 0,
    id: "mention-minimal-editor-list-wrapper",
    onKeyDown: handleKeyDownWrapper,
    onMouseDown: handleOnMouseDownWrapper
  }, attributes.popper), at && options.map((option, i) => /*#__PURE__*/React.createElement("div", {
    key: `${i}${option.value}`,
    "aria-selected": i === valueIndex,
    className: i === valueIndex ? 'bg-gray-100' : '',
    onMouseDown: handleOnMouseDown.bind(undefined, option)
  }, renderLabel(option)))));
};

const {
  useMemo
} = React;
const MENTION_ID = v4();

const MinimalEditor = ({
  editorValue,
  id,
  disabled,
  onEditorChange,
  onKeyDownHandler,
  members,
  editableProps
}) => {
  const pluginsMemo = useMemo(() => {
    return createPlugins([...plugins], {
      components
    });
  }, []);
  const handleOnChange = React.useCallback(newValue => {
    onEditorChange === null || onEditorChange === void 0 ? void 0 : onEditorChange(newValue);
  }, []);
  const editor = usePlateEditorRef(id);
  return /*#__PURE__*/React.createElement(Plate, {
    id: id ? id : MENTION_ID,
    plugins: pluginsMemo,
    editor: editor,
    editableProps: Object.assign({
      placeholder: 'Enter some text...',
      autoFocus: true,
      onKeyDown: onKeyDownHandler,
      readOnly: disabled
    }, editableProps),
    onChange: handleOnChange,
    initialValue: editorValue,
    value: editorValue
  }, /*#__PURE__*/React.createElement(MentionCombobox, {
    onRenderItem: e => /*#__PURE__*/React.createElement("div", {
      key: e.item.key,
      className: "px-8 py-7 rounded-sm min-w-btn-lg w-full hover:bg-gray-100 cursor-pointer align-middle active:text-primary-500 font-medium text-xs flex items-center justify-between"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex font-regular justify-between items-center"
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex items-center"
    }, /*#__PURE__*/React.createElement(Avatar, {
      src: e.item.data.avatar,
      name: e.item.data.name,
      size: "sm",
      wrapperClassName: "border"
    }), /*#__PURE__*/React.createElement("div", {
      className: "flex flex-col ml-10"
    }, /*#__PURE__*/React.createElement("span", {
      className: "text-sm text-black"
    }, e.item.data.name), /*#__PURE__*/React.createElement("span", {
      className: "text-xs"
    }, e.item.data.email)))), /*#__PURE__*/React.createElement("span", {
      className: "ml-26 opacity-75 text-sm"
    }, e.item.data.role)),
    items: (members === null || members === void 0 ? void 0 : members.map(obj => ({
      key: obj.memberId,
      text: `${obj.firstname}`,
      data: {
        name: obj.firstname,
        value: obj.memberId,
        avatar: obj.avatar,
        role: obj.role || '',
        email: obj.email
      }
    }))) || []
  }));
};

const renderLabel = mentionable => {
  var _a;

  return `@${mentionable.name || ((_a = mentionable === null || mentionable === void 0 ? void 0 : mentionable.data) === null || _a === void 0 ? void 0 : _a.name)}`;
};

const components = createPlateUI({
  [ELEMENT_MENTION$1]: withProps(MentionElement, {
    renderLabel: renderLabel,
    styles: {
      root: {
        marginTop: '0.5rem',
        marginBottom: '0.5rem',
        boxShadow: 'none',
        backgroundColor: 'inherit',
        color: '#254DDA'
      }
    }
  })
});
const plugins = [createParagraphPlugin(), createBlockquotePlugin(), createCodeBlockPlugin(), createHeadingPlugin(), // marks
createBoldPlugin(), createItalicPlugin(), createUnderlinePlugin(), createStrikethroughPlugin(), createCodePlugin(), createComboboxPlugin(), createMentionPlugin({
  options: {
    trigger: '@',
    insertSpaceAfterMention: true,
    createMentionNode: item => {
      return {
        data: item.data,
        value: ''
      };
    }
  }
})];

const renderMentionItem = mentionable => {
  return /*#__PURE__*/React.createElement("div", {
    key: mentionable.value,
    className: "px-8 py-7 rounded-sm min-w-btn-lg hover:bg-gray-100 cursor-pointer align-middle active:text-primary-500 font-medium text-xs flex items-center justify-between"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex font-regular justify-between items-center"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center"
  }, /*#__PURE__*/React.createElement(Avatar, {
    src: mentionable === null || mentionable === void 0 ? void 0 : mentionable.avatar,
    name: mentionable === null || mentionable === void 0 ? void 0 : mentionable.name,
    size: "sm",
    wrapperClassName: "border"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col ml-10"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-sm text-black"
  }, mentionable.name), /*#__PURE__*/React.createElement("span", {
    className: "text-xs"
  }, mentionable.email)))), /*#__PURE__*/React.createElement("span", {
    className: "ml-26 opacity-75 text-sm"
  }, mentionable.role));
};
/**
 * Get next index from 0 to max.
 * If index is max, get to 0.
 */


const getNextIndex = (i, max) => i >= max ? 0 : i + 1;
/**
 * Get previous index from 0 to max.
 * If index is 0, get to max.
 */


const getPreviousIndex = (i, max) => i <= 0 ? max : i - 1;

const matchesTriggerAndPattern = (editor, {
  at,
  trigger,
  pattern
}) => {
  // Point at the start of line
  const lineStart = Editor.before(editor, at, {
    unit: 'line'
  }); // Range from before to start

  const beforeRange = lineStart && Editor.range(editor, lineStart, at); // Before text

  const beforeText = getEditorString(editor, beforeRange); // Starts with char and ends with word characters

  const escapedTrigger = escapeRegExp(trigger);
  const beforeRegex = new RegExp(`(?:^|\\s)${escapedTrigger}(${pattern})$`); // Match regex on before text

  const match = !!beforeText && beforeText.match(beforeRegex); // Point at the start of mention

  const mentionStart = match ? Editor.before(editor, at, {
    unit: 'character',
    distance: match[1].length + trigger.length
  }) : null; // Range from mention to start

  const mentionRange = mentionStart && Editor.range(editor, mentionStart, at);
  return {
    range: mentionRange,
    match
  };
};

const ELEMENT_MENTION = 'mention';

const insertMention = (editor, {
  insertSpaceAfterMention,
  data
}) => {
  const mentionNode = Object.assign({
    type: ELEMENT_MENTION,
    children: [{
      text: ''
    }]
  }, data);
  insertNodes(editor, mentionNode);
  Transforms.move(editor);

  if (insertSpaceAfterMention) {
    Transforms.insertText(editor, ' ');
    Transforms.move(editor);
  }
};
/**
 * Enables support for autocompleting @mentions and #tags.
 * When typing a configurable marker, such as @ or #, a select
 * component appears with autocompleted suggestions.
 */


const useMentionPlugin = ({
  mentionables = [],
  maxSuggestions = 10,
  trigger = '@',
  mentionableFilter = search => c => c.value.toLowerCase().includes(search.toLowerCase()),
  mentionableSearchPattern,
  insertSpaceAfterMention,
  onAddMentionCallback
} = {}) => {
  const [targetRange, setTargetRange] = useState$1(null);
  const [valueIndex, setValueIndex] = useState$1(0);
  const [search, setSearch] = useState$1('');
  const values = useMemo$1(() => mentionables.filter(mentionableFilter(search)).slice(0, maxSuggestions), [maxSuggestions, mentionableFilter, mentionables, search]);
  const onAddMention = useCallback$1((editor, data) => {
    if (targetRange !== null) {
      Transforms.select(editor, targetRange);
      insertMention(editor, {
        data,
        insertSpaceAfterMention
      });
      onAddMentionCallback === null || onAddMentionCallback === void 0 ? void 0 : onAddMentionCallback(data);
    }
  }, [targetRange, insertSpaceAfterMention]);
  const onKeyDownMention = useCallback$1(editor => e => {
    if (targetRange) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        return setValueIndex(getNextIndex(valueIndex, values.length - 1));
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        return setValueIndex(getPreviousIndex(valueIndex, values.length - 1));
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        return setTargetRange(null);
      }

      if (['Tab', 'Enter'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        onAddMention(editor, values[valueIndex]);
        return false;
      }
    }
  }, [targetRange, valueIndex, values, onAddMention]);
  const onChangeMention = useCallback$1(editor => () => {
    const {
      selection
    } = editor;

    if (selection && isCollapsed(selection)) {
      const cursor = Range.start(selection);
      const {
        range,
        match: beforeMatch
      } = mentionableSearchPattern ? // new behavior, searches for matching string against pattern right after the trigger
      matchesTriggerAndPattern(editor, {
        at: cursor,
        trigger,
        pattern: mentionableSearchPattern
      }) : // previous behavior. searches for a word after typing the first letter. Kept for backward compatibility.
      isWordAfterTrigger(editor, {
        at: cursor,
        trigger
      });

      if (beforeMatch && isPointAtWordEnd(editor, {
        at: cursor
      })) {
        setTargetRange(range);
        const [, word] = beforeMatch;
        setSearch(word);
        setValueIndex(0);
        return;
      }

      setTargetRange(null);
    }
  }, [setTargetRange, setSearch, setValueIndex, trigger, mentionableSearchPattern]);
  return {
    plugin: useMemo$1(createPluginFactory({
      key: ELEMENT_MENTION$1,
      handlers: {
        onChange: onChangeMention,
        onKeyDown: onKeyDownMention
      },
      isElement: true,
      isVoid: true,
      isInline: true
    }), [onChangeMention, onKeyDownMention]),
    getMentionSelectProps: useCallback$1(() => ({
      at: targetRange,
      valueIndex,
      setValueIndex,
      options: values,
      onClickMention: onAddMention
    }), [onAddMention, targetRange, valueIndex, values]),
    searchValue: search
  };
};

function serialize(node) {
  let str = '';

  if (node.children) {
    node.children.map(child => {
      if (child.text === '') {
        str = `${str}\n`;
      } else if (child.type === 'mention') {
        str = `${str} @[${child.character}](https://skechnote.co/users/${child.character})`;
      } else {
        str = `${str} ${child.text}`;
      }
    });
  }

  return str;
}

function getMDMentionValues(node) {
  let md = '';
  const values = [];

  if (node) {
    node.map((n, index) => {
      n.children.map(obj => {
        var _a, _b;

        if (obj.type === ELEMENT_MENTION$1) {
          md = `${md} @[${((_a = obj === null || obj === void 0 ? void 0 : obj.data) === null || _a === void 0 ? void 0 : _a.name) || 'User'}](https://skechnote.co/users/${obj.value})`;
          values.push((_b = obj === null || obj === void 0 ? void 0 : obj.data) === null || _b === void 0 ? void 0 : _b.value);
        } else {
          md = `${md} ${obj.text}`;
        }
      });

      if (index !== node.length - 1) {
        md = `${md}\n`;
      }
    });
  }

  return {
    md,
    values
  };
}

export { MENTION_ID, MentionSelect, MinimalEditor, getMDMentionValues, renderMentionItem, serialize, useMentionPlugin };
//# sourceMappingURL=index.esm.js.map
